import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { fulfillRedemptionPath } from "@/routes"

/**
 * OKS-61 — Fulfill confirmation dialog (UX Spec §4).
 *
 * Wraps shadcn `AlertDialog` (irreversible-action primitive).
 * Confirm submits an Inertia `<Form action={fulfillRedemptionPath(id)}
 * method="patch">` with empty body — the server reads `Current.brand`
 * + `params[:id]`. **NO shadcn `Form`/`FormField`/`FormMessage`**,
 * **NO RHF / zod** (Crew hard rule); pattern mirrors OKS-60's
 * `RedeemAlertDialog`.
 *
 * # Server-side outcomes
 *
 * Success: `redirect_back` with `flash.success` → Inertia visit
 * re-renders the table → dialog auto-unmounts (parent passes `open=false`
 * via `onClose`) → sonner toast surfaces → pending count chips
 * decrement (server props).
 *
 * Failure (race-loss, row terminalised by another admin):
 * `redirect_back inertia: { errors: { base: <already_actioned-flash> } }`
 * — the controller looks up the `rewards.redemptions.flash.already_actioned`
 * server-side key (config/locales/backend) and sends the resolved string
 * via `inertia.errors.base`.
 * Inertia preserves the page, dialog stays OPEN (UX Spec §4 — "the
 * dialog stays open + inline destructive Alert above the footer").
 * The render-prop `errors.base` drives the inline `<Alert>`. User
 * closes with Cancel → next visit refreshes the queue.
 *
 * # Double-submit guard
 *
 * `disabled={processing}` + `aria-busy` on the Confirm CTA; the
 * Cancel button is also disabled while in-flight so the user can't
 * close mid-submit and stop a successful response from landing.
 */
interface FulfillRedemptionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  redemptionId: number | null
  creatorName: string
  rewardTitle: string
  coinCost: number
}

export function FulfillRedemptionDialog({
  open,
  onOpenChange,
  redemptionId,
  creatorName,
  rewardTitle,
  coinCost,
}: FulfillRedemptionDialogProps) {
  const { t } = useTranslation()

  if (redemptionId === null) {
    return null
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="fulfill-redemption-dialog">
        <Form
          action={fulfillRedemptionPath(redemptionId)}
          method="patch"
          options={{ preserveScroll: true }}
          onSuccess={() => {
            onOpenChange(false)
          }}
        >
          {({ processing, errors }) => (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="fulfill-redemption-dialog-title">
                  {t("rewards.redemptions.fulfill_dialog.title")}
                </AlertDialogTitle>
                <AlertDialogDescription data-testid="fulfill-redemption-dialog-description">
                  {t("rewards.redemptions.fulfill_dialog.body", {
                    creator: creatorName,
                    reward: rewardTitle,
                    count: coinCost,
                  })}
                </AlertDialogDescription>
                <p className="text-muted-foreground text-sm">
                  {t("rewards.redemptions.fulfill_dialog.body_hint")}
                </p>
              </AlertDialogHeader>

              {errors.base ? (
                <Alert
                  variant="destructive"
                  role="alert"
                  data-testid="fulfill-redemption-dialog-error"
                  className="mt-2"
                >
                  <AlertDescription>{errors.base}</AlertDescription>
                </Alert>
              ) : null}

              <AlertDialogFooter>
                <AlertDialogCancel
                  type="button"
                  disabled={processing}
                  data-testid="fulfill-redemption-dialog-cancel"
                >
                  {t("rewards.redemptions.fulfill_dialog.cancel")}
                </AlertDialogCancel>
                {/* OKS-1023 — plain submit `Button`, NOT `AlertDialogAction`:
                    the Action is Base UI `Dialog.Close`, so the click would close
                    the dialog and unmount this `<Form>` with its `processing` and
                    `errors.base` before the server answers. */}
                <Button
                  type="submit"
                  disabled={processing}
                  aria-busy={processing}
                  data-testid="fulfill-redemption-dialog-confirm"
                >
                  {processing ? (
                    <Loader2
                      aria-hidden="true"
                      className="mr-2 size-4 animate-spin"
                    />
                  ) : null}
                  {t("rewards.redemptions.fulfill_dialog.confirm")}
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
